import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IGeoLocationService } from '@intigral/domain/lib/application/services/GeoLocationService';
import { IDeviceService } from '@intigral/domain/lib/application/services/DeviceService';
import { useDataDomainContainer } from '@intigral/domain/lib/presentation/hooks/useDataDomainContainer';
import { useLanguageDataQuery } from '@intigral/domain/lib/presentation/hooks/useLanguageData';
import { Button, Error, Spinner } from '@intigral/shared';
import { getDeviceFingerprint, log } from '@intigral/utils';
import { CACHE_CONFIG_TYPES, errorCodes, STORE_NAME } from '@libs/constants';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useConfiguration } from './hooks/useConfiguration';
import { createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom';
import userActions from './actions/users';
import App from './app';
import h, { setReduxGlobal } from './helpers/common';
import legacyUser from './helpers/user.js';
import { useBrazeInit } from './hooks/useBraze';
import { loadInitApis } from './init';
import { createLanguageRoutes } from './routes';
import { store } from './store';
// REMOVED: Redux config import - all selectors migrated to React Query
import { geolocationInitialize } from './store/reducers/geoLocationSlice';
import { useUserReminders } from './store/reducers/reminderSlice';
import { initLocalaization } from './translations';
import { getUserInfo } from './utils/user';
import { useGuestLandingBootstrap } from './hooks/useGuestLandingBootstrap';
import { useCrossTabSessionSync } from './hooks/useCrossTabSessionSync';
import { useAuthToken } from './utils/userLogin';
import { useUserProfile } from './utils/useUserProfile';
import { isGuestUser } from './config/guestMode';
import { errorMessage } from './config/constants';
import ErrorPage from './pages/ErrorPage';

const reloadPage = () => window.location.reload();

const hasUsableAuthCredentials = (userInfo: ReturnType<typeof getUserInfo>) =>
    Boolean(userInfo?.username && userInfo?.apiKey && userInfo?.authToken && userInfo?.identityToken);

const AppWrapper = () => {
    const dispatch = useDispatch();
    const { isLoading: profileLoading } = useSelector((store: any) => store.profile);

    // React Query configuration hook - replaces localStorage access
    const {
        data: configuration,
        isLoading: isConfigLoading,
        isError: isConfigError,
        error: configError,
    } = useConfiguration();

    // REMOVED: Redux config dispatch - React Query is now the single source of truth
    // All components now use useConfiguration() and useConfigurationSections() hooks

    // This loads app configurations from the API. The callback is responsible for side effects
    // after loading the configurations (e.g. wiring the redux store into helpers).
    const {
        isLoading: isDataDomainLoading,
        isError: isDataDomainError,
        data: dataDomainData,
        error: dataDomainError,
    } = useDataDomainContainer(() => {
        setReduxGlobal(store);
    });

    const dataDomainReady = dataDomainData !== undefined && dataDomainData !== null;
    const { guestBootstrapReady } = useGuestLandingBootstrap(dataDomainReady);

    const { user: userData, isHydratingFromCookie } = useAuthToken(dataDomainReady && guestBootstrapReady);

    const storedUser = getUserInfo();
    const activeUser = userData ?? (storedUser?.username ? storedUser : null);
    const isGuestSession = isGuestUser(activeUser);
    const hasStoredAuthCredentials = hasUsableAuthCredentials(storedUser);

    useCrossTabSessionSync({
        enabled: activeUser !== null,
        isGuest: isGuestSession,
    });

    const isAppConfigReady = !isDataDomainLoading && !isConfigLoading && !!dataDomainData && !!configuration;
    useBrazeInit(userData, isAppConfigReady);
    // const { error: subscriptionError, isError: isSubscriptionError } = useUserSubscriptionData(userData !== null);

    // Loads all language data, and initializes it inside i18next context
    const {
        isLoading: isLanguageLoading,
        isError: isLanguageError,
        error: languageError,
        data: languageData,
    } = useLanguageDataQuery('en', dataDomainData !== null && dataDomainData !== undefined, (languageData) => {
        h.setLang({ languages: languageData });
        initLocalaization(languageData);
    });

    const {
        selectedProfile,
        profilesList,
        isProfilesLoading,
        isInitializing: isProfileInitializing,
    } = useUserProfile(activeUser);

    const {
        isLoading: isDeviceLoading,
        isError: isDeviceError,
        data: deviceRegistrationResult,
        error: deviceError,
    } = useQuery({
        queryKey: ['device-registration'],
        enabled:
            dataDomainData !== null &&
            dataDomainData !== undefined &&
            activeUser !== null &&
            !isGuestSession &&
            hasStoredAuthCredentials,
        queryFn: async () => {
            const userInfo = getUserInfo();
            if (!hasUsableAuthCredentials(userInfo)) {
                // Cross-tab logout can clear storage while in-memory session still looks active.
                return { sessionMissing: true };
            }
            try {
                const deviceService = container.resolve<IDeviceService>(DI.SERVICES.IDeviceService);

                const fingerprint = getDeviceFingerprint(userInfo.username);

                const deviceData = {
                    ...legacyUser.getUserDevice(),
                    fingerprint,
                };
                const device = await deviceService.addDevice(
                    userInfo.username,
                    userInfo.apiKey,
                    deviceData,
                    userInfo.authToken,
                    userInfo.identityToken,
                    { retries: 3 }
                );

                h.setStore(STORE_NAME.DEVICE, device);

                return { device };
            } catch (error) {
                if (error?.data?.code === errorCodes.MAX_DEVICES) return { maxDeviceReached: true };
                if (error?.data?.code === errorCodes.EXPIRED_TOKEN) {
                    // TODO: Display Refresh token popup
                    return {};
                }
                if (!hasUsableAuthCredentials(getUserInfo())) {
                    return { sessionMissing: true };
                }
                throw error;
            }
        },
    });

    // LOAD USER LOCATION
    const {
        data: locationData,
        error: locationError,
        isLoading: isLocationLoading,
        isError: isLocationError,
    } = useQuery({
        queryKey: ['user-location'],
        enabled:
            dataDomainData !== null &&
            dataDomainData !== undefined &&
            activeUser !== null &&
            !isGuestSession &&
            hasStoredAuthCredentials,
        queryFn: async () => {
            try {
                const location = await container
                    .resolve<IGeoLocationService>(DI.SERVICES.IGeoLocationService)
                    .getMyLocation();
                dispatch(geolocationInitialize(location.data));
                return location;
            } catch (error) {
                const fallback = configuration?.defaultLocation ?? null;
                dispatch(geolocationInitialize(fallback));
                return { data: fallback };
            }
        },
    });

    // LEGACY INIT API THAT WILL BE REFACTORED AFTER CHECKING USER FLOW
    const {
        isLoading: isLegacyContextLoading,
        isError: isLegacyContextError,
        data: legacyContextData,
        error: legacyContextError,
    } = useQuery<any>({
        queryKey: ['initialization', 'user-and-device'],
        enabled:
            dataDomainData !== null &&
            dataDomainData !== undefined &&
            configuration !== undefined &&
            activeUser !== null &&
            (isGuestSession ||
                (deviceRegistrationResult !== undefined &&
                    deviceRegistrationResult.device !== undefined &&
                    !deviceRegistrationResult.maxDeviceReached)),
        queryFn: async () => {
            try {
                const context = await loadInitApis(configuration);
                return context;
            } catch (error) {
                throw error;
            }
        },
    });

    const { isError: isReminderLoadError, error: reminderError } = useUserReminders(
        { detailed: false },
        getUserInfo(),
        activeUser !== null
    );

    if (dataDomainData?.success === false && !isGuestUser(getUserInfo())) {
        userActions.logoutUser();
    }

    const waitingOnSignedInUserSetup =
        !isGuestSession &&
        (isLegacyContextLoading ||
            isDeviceLoading ||
            isProfilesLoading ||
            isProfileInitializing ||
            isLocationLoading ||
            profileLoading);

    const isBooting =
        !guestBootstrapReady ||
        isDataDomainLoading ||
        isLanguageLoading ||
        isConfigLoading ||
        isHydratingFromCookie ||
        !activeUser ||
        waitingOnSignedInUserSetup;

    if (isBooting || deviceRegistrationResult?.sessionMissing) {
        // sessionMissing: storage was cleared (e.g. logout in another tab); keep spinner while redirect runs
        return <Spinner opaqueBg />;
    }

    // Translations are unavailable when the language API fails, so the CTA label can't come from i18n
    if (isLanguageError) {
        return (
            <Error
                icon="alert"
                iconColor="red"
                errorPairs={[
                    {
                        title: 'Ooops..',
                        text: h.getLang() === 'ar' ? errorMessage.MSG_AR : errorMessage.MSG_EN,
                    },
                ]}
            >
                <Button compact onClick={reloadPage}>
                    {h.getLang() === 'ar' ? 'تحديث الصفحة' : 'Refresh page'}
                </Button>
            </Error>
        );
    }

    if (isConfigError || isDeviceError || isDataDomainError || isLegacyContextError) {
        return <ErrorPage errorCtaHandler={reloadPage} />;
    }

    return (
        <RouterProvider
            router={createBrowserRouter([
                {
                    path: '/',
                    element: (
                        <App
                            context={{
                                ...legacyContextData,
                                ...deviceRegistrationResult,
                                selectedProfile,
                                profiles: profilesList ?? [],
                                // consent: ConsentData,
                                location: locationData,
                                user: legacyUser.getUserInfo(),
                            }}
                        />
                    ),
                    // action: getInitialData,
                    id: 'root',
                    children: createRoutesFromElements(
                        createLanguageRoutes({
                            ...legacyContextData,
                            ...deviceRegistrationResult,
                            profiles: profilesList ?? [],
                            selectedProfile,
                            // consent: ConsentData,
                            location: locationData,
                            user: legacyUser.getUserInfo(),
                        })
                    ),
                },
            ])}
        />
    );
};

export default AppWrapper;
